import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { speechRecognitionLang } from '../../constants/chatLanguages';
import { readBlobDurationSeconds } from '../../utils/fileUtils';
import {
  canLiveNativeCaption,
  canNativeCaption,
  createPcmTap,
  encodeWav,
  mergeFloat32,
  resampleTo16k,
} from '../../utils/nativeCaptionAudio';
import PresignedAudio from '../common/PresignedAudio';

const SpeechRecognitionAPI =
  typeof window !== 'undefined'
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

const isElectronShell = () =>
  typeof window !== 'undefined' && Boolean(window.workloopRealm);

const pickRecorderMime = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || '';
};

const extensionForMime = (mime) => {
  if (!mime) return 'webm';
  if (mime.includes('mp4') || mime.includes('m4a') || mime.includes('aac')) return 'm4a';
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('mpeg') || mime.includes('mp3')) return 'mp3';
  if (mime.includes('wav')) return 'wav';
  return 'webm';
};

const formatTimer = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

const mergeTranscript = (prev, next) => {
  const left = String(prev || '').replace(/\s+/g, ' ').trim();
  const right = String(next || '').replace(/\s+/g, ' ').trim();
  if (!left) return right;
  if (!right) return left;
  if (left.includes(right)) return left;
  if (right.startsWith(left)) return right;
  const prevWords = left.split(' ');
  const nextWords = right.split(' ');
  const max = Math.min(prevWords.length, nextWords.length, 10);
  let overlap = 0;
  for (let count = max; count > 0; count -= 1) {
    if (prevWords.slice(-count).join(' ').toLowerCase() === nextWords.slice(0, count).join(' ').toLowerCase()) {
      overlap = count;
      break;
    }
  }
  return `${left} ${nextWords.slice(overlap).join(' ')}`.replace(/\s+/g, ' ').trim();
};

export const captionToHtml = (caption) => {
  const text = String(caption || '').trim();
  if (!text) return '';
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  return `<p>${escaped.replace(/\n/g, '<br/>')}</p>`;
};

export const blobToVoiceFile = async (blob, durationSeconds) => {
  const rawType = String(blob?.type || 'audio/webm');
  const mime = (rawType.split(';')[0] || 'audio/webm').trim() || 'audio/webm';
  const ext = extensionForMime(mime);
  const secs = Number(durationSeconds);
  const durationPart = Number.isFinite(secs) && secs >= 0.5
    ? `-${Math.max(1, Math.round(secs))}s`
    : '';
  const bytes = blob instanceof Blob ? await blob.arrayBuffer() : blob;
  return new File([bytes], `voice-note-${Date.now()}${durationPart}.${ext}`, {
    type: mime.startsWith('audio/') ? mime : 'audio/webm',
    lastModified: Date.now(),
  });
};

export function useVoiceRecorder({ onPermissionError } = {}) {
  const preferredLanguage = useSelector((state) => state.auth?.user?.preferredLanguage || 'en');
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [liveCaption, setLiveCaption] = useState('');
  const [speechHint, setSpeechHint] = useState('');
  const [captionPrompt, setCaptionPrompt] = useState('idle');
  const [review, setReview] = useState(null);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const recognitionRef = useRef(null);
  const timerRef = useRef(null);
  const finalCaptionRef = useRef('');
  const interimCaptionRef = useRef('');
  const stopResolverRef = useRef(null);
  const cancelledRef = useRef(false);
  const secondsRef = useRef(0);
  const recordingStartedAtRef = useRef(0);
  const captionDraftRef = useRef('');
  const finishOnceRef = useRef(false);
  const recordingActiveRef = useRef(false);
  const captionPromptRef = useRef('idle');
  const speechFatalRef = useRef(false);
  const pcmChunksRef = useRef([]);
  const pcmCursorRef = useRef(0);
  const nativeCaptionTimerRef = useRef(null);
  const nativeAudioRef = useRef(null);
  const nativeLivePushRef = useRef(false);
  const nativeLiveUnsubRef = useRef(null);
  const [captioning, setCaptioning] = useState(false);

  const speechSupported = Boolean(SpeechRecognitionAPI) || canNativeCaption();
  const recorderSupported =
    typeof window !== 'undefined'
    && typeof MediaRecorder !== 'undefined'
    && typeof navigator !== 'undefined'
    && navigator.mediaDevices?.getUserMedia;

  const stopRecognition = useCallback((clearHandlers = true) => {
    const recognition = recognitionRef.current;
    if (clearHandlers) {
      recognitionRef.current = null;
    }
    if (!recognition) return;
    if (clearHandlers) {
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
    }
    try {
      if (typeof recognition.abort === 'function') {
        recognition.abort();
      } else {
        recognition.stop();
      }
    } catch {
      // already stopped
    }
  }, []);

  const setCaptionPromptState = (next) => {
    captionPromptRef.current = next;
    setCaptionPrompt(next);
  };

  const startSpeechRecognition = useCallback(() => {
    if (isElectronShell() || !SpeechRecognitionAPI) {
      return false;
    }
    if (speechFatalRef.current) return false;
    stopRecognition(true);
    try {
      const recognition = new SpeechRecognitionAPI();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;
      recognition.lang = speechRecognitionLang(preferredLanguage);
      recognition.onstart = () => {
        setCaptionPromptState('granted');
        setSpeechHint('');
      };
      recognition.onresult = (event) => {
        let interim = '';
        let finalText = finalCaptionRef.current;
        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const piece = event.results[i][0]?.transcript || '';
          if (event.results[i].isFinal) {
            finalText = `${finalText} ${piece}`.replace(/\s+/g, ' ').trim();
          } else {
            interim += piece;
          }
        }
        finalCaptionRef.current = finalText;
        interimCaptionRef.current = interim;
        const live = `${finalText} ${interim}`.replace(/\s+/g, ' ').trim();
        captionDraftRef.current = live;
        setLiveCaption(live);
        setCaptionPromptState('granted');
        setSpeechHint('');
      };
      recognition.onerror = (event) => {
        const err = event?.error;
        if (err === 'no-speech' || err === 'aborted') return;
        if (
          err === 'not-allowed'
          || err === 'service-not-allowed'
          || err === 'network'
          || err === 'audio-capture'
        ) {
          speechFatalRef.current = true;
          try {
            recognition.onend = null;
            recognition.abort?.();
          } catch {
            // ignore
          }
          recognitionRef.current = null;
          if (err === 'not-allowed' || err === 'service-not-allowed') {
            setCaptionPromptState('denied');
            setSpeechHint('');
          } else {
            setCaptionPromptState('failed');
            setSpeechHint('');
          }
        }
      };
      recognition.onend = () => {
        if (
          speechFatalRef.current
          || !recordingActiveRef.current
          || recognitionRef.current !== recognition
        ) {
          return;
        }
        try {
          recognition.start();
        } catch {
          speechFatalRef.current = true;
        }
      };
      recognitionRef.current = recognition;
      recognition.start();
      return true;
    } catch {
      speechFatalRef.current = true;
      setCaptionPromptState('failed');
      setSpeechHint('');
      return false;
    }
  }, [stopRecognition, preferredLanguage]);

  const declineCaptionPermission = useCallback(() => {
    stopRecognition(true);
    captionPromptRef.current = 'denied';
    setCaptionPrompt('denied');
    setSpeechHint('');
  }, [stopRecognition]);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const stopNativeCaptions = useCallback(() => {
    if (nativeCaptionTimerRef.current) {
      window.clearTimeout(nativeCaptionTimerRef.current);
      nativeCaptionTimerRef.current = null;
    }
    nativeLivePushRef.current = false;
    try { nativeLiveUnsubRef.current?.(); } catch { /* ignore */ }
    nativeLiveUnsubRef.current = null;
    const native = nativeAudioRef.current;
    nativeAudioRef.current = null;
    try { native?.disconnect?.(); } catch { /* ignore */ }
    try { window.workloopRealm?.stopLiveCaption?.(); } catch { /* ignore */ }
  }, []);

  const transcribePcm = useCallback(async (float32, sampleRate) => {
    if (!canNativeCaption() || !float32?.length) return '';
    try {
      return String(await window.workloopRealm.transcribeWav(encodeWav(float32, sampleRate)) || '').trim();
    } catch {
      return '';
    }
  }, []);

  const transcribeFullPcm = useCallback(async (float32, sampleRate) => {
    const pcm16 = resampleTo16k(float32, sampleRate);
    if (!pcm16?.length) return '';
    const windowSamples = 16000 * 12;
    const hop = 16000 * 10;
    if (pcm16.length <= windowSamples) {
      return transcribePcm(pcm16, 16000);
    }
    let merged = '';
    for (let start = 0; start < pcm16.length; start += hop) {
      const end = Math.min(pcm16.length, start + windowSamples);
      const text = await transcribePcm(pcm16.subarray(start, end), 16000);
      merged = mergeTranscript(merged, text);
      if (end >= pcm16.length) break;
    }
    return merged;
  }, [transcribePcm]);

  const startPcmCapture = useCallback(async (stream) => {
    if (!stream || !canNativeCaption()) return;
    stopNativeCaptions();
    pcmChunksRef.current = [];
    pcmCursorRef.current = 0;
    nativeLivePushRef.current = false;
    if (canLiveNativeCaption()) {
      try {
        const unsubCaption = window.workloopRealm.onLiveCaption?.((text) => {
          if (!recordingActiveRef.current) return;
          const next = String(text || '').trim();
          if (!next) return;
          captionDraftRef.current = next;
          setLiveCaption(next);
        });
        const unsubEnded = window.workloopRealm.onLiveCaptionEnded?.(() => {
          nativeLivePushRef.current = false;
        });
        nativeLiveUnsubRef.current = () => {
          try { unsubCaption?.(); } catch { /* ignore */ }
          try { unsubEnded?.(); } catch { /* ignore */ }
        };
        const started = await window.workloopRealm.startLiveCaption();
        nativeLivePushRef.current = Boolean(recordingActiveRef.current && started && started.ok !== false);
        if (!nativeLivePushRef.current) {
          try { await window.workloopRealm.stopLiveCaption?.(); } catch { /* ignore */ }
        }
      } catch {
        nativeLivePushRef.current = false;
      }
    }
    const tap = createPcmTap(stream, {
      onFloat32: (copy, sampleRate) => {
        if (!recordingActiveRef.current) return;
        pcmChunksRef.current.push(copy);
        const maxChunks = Math.ceil(((sampleRate || 16000) * 180) / 2048);
        if (pcmChunksRef.current.length > maxChunks) {
          pcmChunksRef.current = pcmChunksRef.current.slice(-maxChunks);
        }
      },
      shouldPushLive: () => nativeLivePushRef.current && recordingActiveRef.current,
    });
    nativeAudioRef.current = tap;
    const tick = async () => {
      if (!recordingActiveRef.current) return;
      if (!nativeLivePushRef.current && pcmChunksRef.current.length && canNativeCaption()) {
        const rate = tap?.context?.sampleRate || 16000;
        const all = mergeFloat32(pcmChunksRef.current);
        const keep = Math.max(1, Math.floor(rate * 8));
        const windowed = all.length > keep ? all.subarray(all.length - keep) : all;
        try {
          const text = String(await window.workloopRealm.transcribeWav(
            encodeWav(resampleTo16k(windowed, rate), 16000),
          ) || '').trim();
          if (text && recordingActiveRef.current) {
            captionDraftRef.current = text;
            setLiveCaption(text);
          }
        } catch {
          // ignore
        }
      }
      if (recordingActiveRef.current) {
        nativeCaptionTimerRef.current = window.setTimeout(tick, nativeLivePushRef.current ? 2400 : 1400);
      }
    };
    nativeCaptionTimerRef.current = window.setTimeout(tick, 900);
  }, [stopNativeCaptions]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const combinedCaption = () =>
    `${finalCaptionRef.current} ${interimCaptionRef.current}`.replace(/\s+/g, ' ').trim();

  const rememberCaption = (value) => {
    const next = String(value || '').replace(/\s+/g, ' ').trim();
    captionDraftRef.current = next;
    return next;
  };

  const flushSpeechCaption = () => new Promise((resolve) => {
    const recognition = recognitionRef.current;
    if (!recognition) {
      resolve(combinedCaption());
      return;
    }
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      recognition.onend = null;
      recognitionRef.current = null;
      resolve(combinedCaption());
    };
    recognition.onend = () => finish();
    try {
      recognition.stop();
    } catch {
      finish();
      return;
    }
    window.setTimeout(finish, 700);
  });

  const finishRecording = useCallback((blob) => {
    if (finishOnceRef.current) return;
    finishOnceRef.current = true;
    recordingActiveRef.current = false;
    const sampleRate = nativeAudioRef.current?.context?.sampleRate || 16000;
    const pcmCopy = pcmChunksRef.current.length ? mergeFloat32(pcmChunksRef.current) : null;
    stopNativeCaptions();
    stopStream();
    clearTimer();
    setRecording(false);
    setSeconds(0);
    const spoken = String(finalCaptionRef.current || captionDraftRef.current || '').trim();
    const caption = rememberCaption(spoken);
    stopRecognition(true);
    finalCaptionRef.current = caption;
    interimCaptionRef.current = '';
    setLiveCaption(caption);
    const audioBlob = blob && blob.size > 0
      ? blob
      : (chunksRef.current.length
        ? new Blob(chunksRef.current, { type: pickRecorderMime() || 'audio/webm' })
        : null);
    if (!audioBlob || audioBlob.size === 0) {
      setCaptioning(false);
      stopResolverRef.current?.(null);
      stopResolverRef.current = null;
      finishOnceRef.current = false;
      return;
    }
    const elapsedSec = recordingStartedAtRef.current
      ? Math.max((Date.now() - recordingStartedAtRef.current) / 1000, 0.1)
      : (secondsRef.current > 0 ? secondsRef.current : undefined);
    const url = URL.createObjectURL(audioBlob);
    const next = {
      blob: audioBlob,
      url,
      caption,
      durationSeconds: elapsedSec,
    };
    setReview(next);
    stopResolverRef.current?.(next);
    stopResolverRef.current = null;
    readBlobDurationSeconds(audioBlob).then((decoded) => {
      if (!decoded) return;
      setReview((prev) => (
        prev && prev.url === url
          ? { ...prev, durationSeconds: decoded, caption: prev.caption || caption }
          : prev
      ));
    });
    if (canNativeCaption() && pcmCopy?.length) {
      setCaptioning(true);
      transcribeFullPcm(pcmCopy, sampleRate).then((text) => {
        const late = String(text || '').trim();
        if (late) {
          rememberCaption(late);
          setLiveCaption(late);
          setReview((prev) => (prev && prev.url === url ? { ...prev, caption: late } : prev));
        }
      }).finally(() => {
        setCaptioning(false);
      });
    } else {
      setCaptioning(false);
    }
  }, [clearTimer, stopNativeCaptions, stopRecognition, stopStream, transcribeFullPcm]);

  const stopRecording = useCallback(() => {
    if (!recording && !mediaRecorderRef.current) {
      return Promise.resolve(review);
    }
    return new Promise((resolve) => {
      stopResolverRef.current = resolve;
      const recorder = mediaRecorderRef.current;
      const fallback = window.setTimeout(() => {
        if (stopResolverRef.current !== resolve) return;
        finishRecording(new Blob(chunksRef.current, { type: pickRecorderMime() || 'audio/webm' }));
      }, 1200);
      const origResolve = resolve;
      stopResolverRef.current = (value) => {
        window.clearTimeout(fallback);
        origResolve(value);
      };
      if (recorder && recorder.state !== 'inactive') {
        try {
          if (typeof recorder.requestData === 'function' && recorder.state === 'recording') {
            recorder.requestData();
          }
          recorder.stop();
        } catch {
          finishRecording(new Blob(chunksRef.current, { type: pickRecorderMime() || 'audio/webm' }));
        }
      } else {
        finishRecording(new Blob(chunksRef.current, { type: pickRecorderMime() || 'audio/webm' }));
      }
    });
  }, [finishRecording, recording, review]);

  const discardReview = useCallback(() => {
    if (review?.url) {
      URL.revokeObjectURL(review.url);
    }
    setReview(null);
  }, [review]);

  const updateReviewCaption = useCallback((caption) => {
    captionDraftRef.current = caption;
    setReview((prev) => (prev ? { ...prev, caption } : prev));
  }, []);

  const cancelRecording = useCallback(() => {
    cancelledRef.current = true;
    recordingActiveRef.current = false;
    chunksRef.current = [];
    const recorder = mediaRecorderRef.current;
    mediaRecorderRef.current = null;
    stopRecognition();
    stopNativeCaptions();
    stopStream();
    clearTimer();
    setRecording(false);
    setSeconds(0);
    secondsRef.current = 0;
    recordingStartedAtRef.current = 0;
    setLiveCaption('');
    setSpeechHint('');
    captionPromptRef.current = 'idle';
    setCaptionPrompt('idle');
    setCaptioning(false);
    finalCaptionRef.current = '';
    interimCaptionRef.current = '';
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {
        // ignore
      }
    }
    discardReview();
  }, [clearTimer, discardReview, stopNativeCaptions, stopRecognition, stopStream]);

  const startRecording = useCallback(async () => {
    if (!recorderSupported) {
      onPermissionError?.('Voice notes need a browser that supports audio recording.');
      return;
    }
    cancelledRef.current = false;
    finishOnceRef.current = false;
    recordingActiveRef.current = false;
    speechFatalRef.current = false;
    discardReview();
    chunksRef.current = [];
    finalCaptionRef.current = '';
    interimCaptionRef.current = '';
    captionDraftRef.current = '';
    setCaptionPromptState('idle');
    setLiveCaption('');
    setSpeechHint('');
    setSeconds(0);
    secondsRef.current = 0;
    recordingStartedAtRef.current = 0;
    pcmChunksRef.current = [];
    pcmCursorRef.current = 0;
    setCaptioning(false);

    if (typeof window !== 'undefined' && window.workloopRealm?.ensureMicrophoneAccess) {
      try {
        const allowed = await window.workloopRealm.ensureMicrophoneAccess();
        if (!allowed) {
          recordingActiveRef.current = false;
          stopRecognition(true);
          onPermissionError?.('Microphone access was denied. Allow the mic in System Settings to record a voice note.');
          return;
        }
      } catch {
        // Fall through to getUserMedia; Chromium may still prompt.
      }
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch {
      recordingActiveRef.current = false;
      stopRecognition(true);
      onPermissionError?.('Microphone access was denied. Allow the mic to record a voice note.');
      return;
    }

    streamRef.current = stream;
    const mimeType = pickRecorderMime();
    let recorder;
    try {
      recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
    } catch {
      stopStream();
      onPermissionError?.('Could not start audio recording in this browser.');
      return;
    }

    mediaRecorderRef.current = recorder;
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };
    recorder.onstop = () => {
      mediaRecorderRef.current = null;
      if (cancelledRef.current) {
        cancelledRef.current = false;
        chunksRef.current = [];
        stopResolverRef.current?.(null);
        stopResolverRef.current = null;
        return;
      }
      const type = recorder.mimeType || mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type });
      finishRecording(blob);
    };

    recorder.start(250);
    recordingActiveRef.current = true;
    setRecording(true);
    setCaptionPromptState('granted');
    if (isElectronShell() && canNativeCaption()) {
      startPcmCapture(stream).catch(() => {});
    } else {
      startSpeechRecognition();
    }
    secondsRef.current = 0;
    recordingStartedAtRef.current = Date.now();
    timerRef.current = setInterval(() => {
      secondsRef.current += 1;
      setSeconds(secondsRef.current);
    }, 1000);
  }, [discardReview, finishRecording, onPermissionError, recorderSupported, startPcmCapture, startSpeechRecognition, stopRecognition, stopStream]);

  useEffect(() => () => {
    chunksRef.current = [];
    stopRecognition();
    stopNativeCaptions();
    stopStream();
    clearTimer();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        // ignore
      }
    }
  }, [clearTimer, stopNativeCaptions, stopRecognition, stopStream]);

  return {
    recording,
    seconds,
    liveCaption,
    captioning,
    speechHint,
    captionPrompt,
    review,
    speechSupported,
    recorderSupported,
    startRecording,
    startSpeechRecognition,
    declineCaptionPermission,
    stopRecording,
    cancelRecording,
    discardReview,
    updateReviewCaption,
    getCaption: () => captionDraftRef.current || review?.caption || '',
  };
}

export const VoiceNoteBanner = ({
  recording,
  seconds,
  caption,
  captionAfterStop = false,
  speechSupported,
  speechHint,
  captionPrompt,
  onAllowCaptions,
  onDeclineCaptions,
  onStop,
  onCancel,
}) => {
  if (!recording) return null;

  return (
    <div className="mx-0 mb-2 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/40 px-3 py-2">
      <div className="flex items-center gap-3">
        <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-red-700 dark:text-red-300">Recording</p>
            <p className="text-xs tabular-nums text-red-600/80 dark:text-red-400">{formatTimer(seconds)}</p>
          </div>
          <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-100 break-words whitespace-pre-wrap">
            {caption
              || (captionAfterStop
                ? 'Caption will be added after you stop.'
                : 'Listening for caption…')}
          </p>
          {captionPrompt === 'pending' && !caption ? (
            <div className="mt-1.5 flex items-center gap-2">
              <button
                type="button"
                onClick={onAllowCaptions}
                className="rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-700"
              >
                Allow captions
              </button>
              <button
                type="button"
                onClick={onDeclineCaptions}
                className="rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-red-100 dark:text-gray-300 dark:hover:bg-red-900/40"
              >
                Don&apos;t allow
              </button>
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onStop}
          className="flex-shrink-0 rounded-md bg-red-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-red-700"
        >
          Stop
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-shrink-0 rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-red-100 dark:text-gray-300 dark:hover:bg-red-900/40"
        >
          Discard
        </button>
      </div>
    </div>
  );
};

export const VoiceNoteMicButton = ({ recording, disabled, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`p-1.5 rounded-md transition-colors ${
      recording
        ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300'
        : 'hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    title={recording ? 'Stop recording' : 'Record voice note'}
    aria-label={recording ? 'Stop recording' : 'Record voice note'}
    aria-pressed={recording}
  >
    {recording ? (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6 6h12v12H6z" />
      </svg>
    ) : (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14a3 3 0 003-3V7a3 3 0 10-6 0v4a3 3 0 003 3zm6-3a6 6 0 01-12 0m6 6v3m-4 0h8" />
      </svg>
    )}
  </button>
);

export const VoiceNoteSendModal = ({
  isOpen,
  caption,
  captioning = false,
  onCaptionChange,
  audioUrl,
  durationSeconds,
  sending,
  speechSupported,
  speechHint,
  onSendWithAudio,
  onSendAudioOnly,
  onSendCaptionOnly,
  onDiscard,
  zIndexClass = 'z-[1000]',
}) => {
  const [draft, setDraft] = useState(caption || '');

  useEffect(() => {
    if (isOpen) setDraft(caption || '');
  }, [isOpen, caption]);

  if (!isOpen) return null;

  const setCaption = (value) => {
    setDraft(value);
    onCaptionChange?.(value);
  };

  return createPortal(
    <div className={`fixed inset-0 ${zIndexClass} overflow-y-auto`}>
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={sending ? undefined : onDiscard} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg rounded-lg bg-white dark:bg-gray-800 shadow-xl">
          <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Send voice note?</h3>
            <button
              type="button"
              onClick={onDiscard}
              disabled={sending}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-5 space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Send audio with caption, audio only, or caption only.
            </p>
            {audioUrl ? (
              <PresignedAudio fileKey={audioUrl} durationSeconds={durationSeconds} />
            ) : null}
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Caption</span>
              <textarea
                value={draft}
                onChange={(event) => setCaption(event.target.value)}
                rows={4}
                placeholder={
                  captioning
                    ? 'Creating caption…'
                    : (speechSupported ? 'Edit the spoken caption…' : 'Type a caption…')
                }
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
              />
            </label>
          </div>
          <div className="flex flex-col gap-2 p-5 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-nowrap items-center gap-2">
              <button
                type="button"
                onClick={() => onSendCaptionOnly(draft)}
                disabled={sending || !String(draft || '').trim()}
                className="flex-1 whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Caption only
              </button>
              <button
                type="button"
                onClick={() => onSendAudioOnly(draft)}
                disabled={sending || !audioUrl}
                className="flex-1 whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Audio only
              </button>
              <button
                type="button"
                onClick={() => onSendWithAudio(draft)}
                disabled={sending}
                className="flex-1 whitespace-nowrap px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {sending ? 'Sending…' : 'Audio with caption'}
              </button>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onDiscard}
                disabled={sending}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
